import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
  CardFooter
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  CalendarIcon, 
  Plus, 
  Target, 
  Users, 
  MessageSquare, 
  ImageIcon, 
  Video, 
  Music, 
  Globe, 
  Instagram, 
  Youtube, 
  Facebook, 
  Twitter, 
  Headphones, 
  Mic, 
  Save,
  FileSpreadsheet,
  CheckSquare,
  SendHorizontal,
  FileText,
  Palette
} from 'lucide-react';
import { format } from 'date-fns';
import CanvaDesignTool from './CanvaDesignTool';

// Campaign types
const campaignTypes = [
  { id: 'single', name: 'Single Release', icon: <Music className="h-4 w-4" /> },
  { id: 'ep', name: 'EP Release', icon: <Music className="h-4 w-4" /> },
  { id: 'album', name: 'Album Release', icon: <Headphones className="h-4 w-4" /> },
  { id: 'video', name: 'Music Video', icon: <Video className="h-4 w-4" /> },
  { id: 'tour', name: 'Tour/Concert', icon: <Mic className="h-4 w-4" /> },
  { id: 'collab', name: 'Collaboration', icon: <Users className="h-4 w-4" /> },
  { id: 'other', name: 'Custom Campaign', icon: <Target className="h-4 w-4" /> },
];

// Social platforms
const platforms = [
  { id: 'instagram', name: 'Instagram', icon: <Instagram className="h-4 w-4" /> },
  { id: 'youtube', name: 'YouTube', icon: <Youtube className="h-4 w-4" /> },
  { id: 'facebook', name: 'Facebook', icon: <Facebook className="h-4 w-4" /> },
  { id: 'twitter', name: 'Twitter', icon: <Twitter className="h-4 w-4" /> },
  { id: 'tiktok', name: 'TikTok', icon: <Video className="h-4 w-4" /> },
  { id: 'spotify', name: 'Spotify', icon: <Music className="h-4 w-4" /> },
  { id: 'website', name: 'Website', icon: <Globe className="h-4 w-4" /> },
];

// Campaign phases
const defaultPhases = [
  {
    id: 'pre',
    name: 'Pre-Launch',
    description: 'Build anticipation before your release',
    tasks: [
      { id: 'pre-1', name: 'Create teaser content', completed: false },
      { id: 'pre-2', name: 'Announce release date', completed: false },
      { id: 'pre-3', name: 'Share behind the scenes', completed: false },
      { id: 'pre-4', name: 'Email newsletter announcement', completed: false }
    ]
  },
  {
    id: 'launch',
    name: 'Launch',
    description: 'Maximum visibility on release day',
    tasks: [
      { id: 'launch-1', name: 'Post release announcement', completed: false },
      { id: 'launch-2', name: 'Share on all platforms', completed: false },
      { id: 'launch-3', name: 'Go live on Instagram/TikTok', completed: false },
      { id: 'launch-4', name: 'Email release notification', completed: false }
    ]
  },
  {
    id: 'post',
    name: 'Post-Launch',
    description: 'Maintain momentum after release',
    tasks: [
      { id: 'post-1', name: 'Share fan reactions', completed: false },
      { id: 'post-2', name: 'Run social media ads', completed: false },
      { id: 'post-3', name: 'Post lyric/music video', completed: false },
      { id: 'post-4', name: 'Engage with fan comments', completed: false }
    ]
  }
];

const CampaignPlanner: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const isNewCampaign = searchParams.get('new') === 'true';
  
  const [campaignName, setCampaignName] = useState('');
  const [campaignType, setCampaignType] = useState('');
  const [description, setDescription] = useState('');
  const [objective, setObjective] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [releaseDate, setReleaseDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [budget, setBudget] = useState('');
  const [phases, setPhases] = useState(defaultPhases);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [designAssets, setDesignAssets] = useState<Array<{id: string; url: string; type: string}>>([]);
  
  useEffect(() => {
    if (isNewCampaign) {
      setCampaignName('');
      setCampaignType('');
      setDescription('');
      setObjective('');
      setTargetAudience('');
      setSelectedPlatforms([]);
      setReleaseDate(undefined);
      setEndDate(undefined);
      setBudget('');
      setPhases(defaultPhases);
    }
  }, [isNewCampaign]);
  
  const togglePlatform = (platformId: string) => {
    setSelectedPlatforms(prev => 
      prev.includes(platformId)
        ? prev.filter(id => id !== platformId)
        : [...prev, platformId]
    );
    setHasUnsavedChanges(true);
  };
  
  const toggleTaskCompletion = (phaseId: string, taskId: string) => {
    setPhases(prev => 
      prev.map(phase => 
        phase.id === phaseId
          ? {
              ...phase,
              tasks: phase.tasks.map(task => 
                task.id === taskId
                  ? { ...task, completed: !task.completed }
                  : task
              )
            }
          : phase
      )
    );
    setHasUnsavedChanges(true);
  };
  
  const addTaskToPhase = (phaseId: string, taskName: string) => {
    if (!taskName.trim()) return;
    
    setPhases(prev => 
      prev.map(phase => 
        phase.id === phaseId
          ? {
              ...phase,
              tasks: [
                ...phase.tasks,
                {
                  id: `${phaseId}-${Date.now()}`,
                  name: taskName,
                  completed: false
                }
              ]
            }
          : phase
      )
    );
    setHasUnsavedChanges(true);
  };
  
  const saveCampaign = () => {
    if (!campaignName) {
      toast({
        title: "Missing information",
        description: "Please provide a campaign name",
        variant: "destructive"
      });
      return;
    }
    
    if (!campaignType) {
      toast({
        title: "Missing information",
        description: "Please select a campaign type",
        variant: "destructive"
      });
      return;
    }
    
    if (!releaseDate) {
      toast({
        title: "Missing information",
        description: "Please select a release date",
        variant: "destructive"
      });
      return;
    }
    
    toast({
      title: "Campaign saved",
      description: `${campaignName} has been saved successfully`,
    });
    
    setHasUnsavedChanges(false);
    
    if (isNewCampaign) {
      searchParams.delete('new');
      setSearchParams(searchParams);
    }
  };
  
  const launchCampaign = () => {
    saveCampaign();
    
    toast({
      title: "Campaign launched!",
      description: `${campaignName} is now active. Your marketing journey begins!`,
    });
  };
  
  const exportCampaign = () => {
    toast({
      title: "Campaign exported",
      description: "Your campaign plan has been exported to a spreadsheet",
    });
  };
  
  const isCampaignValid = campaignName && campaignType && releaseDate;
  
  const handleDesignSave = (designUrl: string) => {
    setDesignAssets(prev => [
      ...prev, 
      { 
        id: `design-${Date.now()}`, 
        url: designUrl, 
        type: 'canva' 
      }
    ]);
    setHasUnsavedChanges(true);
    
    toast({
      title: "Design asset added",
      description: "Your design has been added to campaign assets",
    });
  };
  
  return (
    <div className="p-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Campaign Planner</h2>
          <p className="text-muted-foreground">Create and manage your marketing campaigns</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={exportCampaign} 
            disabled={!isCampaignValid} 
            className="gap-2"
          >
            <FileSpreadsheet className="h-4 w-4" />
            Export
          </Button>
          <Button 
            variant="default" 
            onClick={saveCampaign} 
            disabled={!isCampaignValid || !hasUnsavedChanges} 
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            Save
          </Button>
          <Button 
            variant="default" 
            onClick={launchCampaign} 
            disabled={!isCampaignValid}
            className="gap-2 bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700"
          >
            <SendHorizontal className="h-4 w-4" />
            {isNewCampaign ? 'Launch Campaign' : 'Update Campaign'}
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Campaign Details</CardTitle>
              <CardDescription>Define the core elements of your marketing campaign</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="campaign-name">Campaign Name *</Label>
                  <Input 
                    id="campaign-name" 
                    placeholder="e.g., Summer Album Launch" 
                    value={campaignName}
                    onChange={(e) => {
                      setCampaignName(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="campaign-type">Campaign Type *</Label>
                  <Select 
                    value={campaignType} 
                    onValueChange={(value) => {
                      setCampaignType(value);
                      setHasUnsavedChanges(true);
                    }}
                  >
                    <SelectTrigger id="campaign-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {campaignTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          <div className="flex items-center gap-2">
                            {type.icon}
                            <span>{type.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea 
                  id="description" 
                  placeholder="Brief description of your campaign"
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="release-date">Release Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {releaseDate ? format(releaseDate, "PPP") : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={releaseDate}
                        onSelect={(date) => {
                          setReleaseDate(date);
                          setHasUnsavedChanges(true);
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="end-date">End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "PPP") : <span>Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={(date) => {
                          setEndDate(date);
                          setHasUnsavedChanges(true);
                        }}
                        disabled={(date) => 
                          releaseDate ? date < releaseDate : false
                        }
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="objective">Primary Objective</Label>
                  <Select 
                    value={objective} 
                    onValueChange={(value) => {
                      setObjective(value);
                      setHasUnsavedChanges(true);
                    }}
                  >
                    <SelectTrigger id="objective">
                      <SelectValue placeholder="Select objective" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="awareness">Brand Awareness</SelectItem>
                      <SelectItem value="streams">Increase Streams</SelectItem>
                      <SelectItem value="sales">Boost Sales</SelectItem>
                      <SelectItem value="engagement">Fan Engagement</SelectItem>
                      <SelectItem value="growth">Grow Following</SelectItem>
                      <SelectItem value="tickets">Sell Tickets</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="budget">Budget</Label>
                  <Input 
                    id="budget" 
                    placeholder="e.g., $500" 
                    value={budget}
                    onChange={(e) => {
                      setBudget(e.target.value);
                      setHasUnsavedChanges(true);
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Target Audience & Platforms</CardTitle>
              <CardDescription>Define your audience and where to reach them</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="target-audience">Target Audience Description</Label>
                <Textarea 
                  id="target-audience" 
                  placeholder="e.g., 18-35 year-olds interested in indie rock, primarily in urban areas"
                  value={targetAudience}
                  onChange={(e) => {
                    setTargetAudience(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Platforms</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                  {platforms.map((platform) => (
                    <div key={platform.id} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`platform-${platform.id}`}
                        checked={selectedPlatforms.includes(platform.id)}
                        onCheckedChange={() => togglePlatform(platform.id)}
                      />
                      <label
                        htmlFor={`platform-${platform.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-1"
                      >
                        {platform.icon}
                        <span>{platform.name}</span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Campaign Phases & Tasks</CardTitle>
              <CardDescription>Plan your campaign timeline and tasks</CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" defaultValue={['pre', 'launch', 'post']} className="space-y-2">
                {phases.map((phase) => (
                  <AccordionItem key={phase.id} value={phase.id} className="border rounded-md px-4">
                    <AccordionTrigger className="py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{phase.name}</span>
                        <Badge variant="outline" className="ml-2">
                          {phase.tasks.filter(t => t.completed).length}/{phase.tasks.length}
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="py-2">
                        <p className="text-sm text-muted-foreground mb-3">{phase.description}</p>
                        <div className="space-y-2">
                          {phase.tasks.map((task) => (
                            <div key={task.id} className="flex items-center gap-2 p-2 rounded-md hover:bg-muted/50">
                              <Checkbox 
                                id={task.id}
                                checked={task.completed}
                                onCheckedChange={() => toggleTaskCompletion(phase.id, task.id)}
                              />
                              <label
                                htmlFor={task.id}
                                className={`flex-1 text-sm ${task.completed ? 'line-through text-muted-foreground' : ''}`}
                              >
                                {task.name}
                              </label>
                            </div>
                          ))}
                        </div>
                        
                        <div className="flex items-center gap-2 mt-4">
                          <Input 
                            placeholder="Add new task..."
                            className="text-sm"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                addTaskToPhase(phase.id, e.currentTarget.value);
                                e.currentTarget.value = '';
                              }
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              const input = e.currentTarget.previousSibling as HTMLInputElement;
                              addTaskToPhase(phase.id, input.value);
                              input.value = '';
                            }}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
        
        <div className="space-y-6">
          <CanvaDesignTool 
            onDesignSave={handleDesignSave} 
            className="bg-gradient-to-br from-background to-purple-900/10 backdrop-blur-sm border-purple-500/20 shadow-lg"
          />
          
          {designAssets.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5 text-purple-500" />
                  Campaign Assets
                </CardTitle>
                <CardDescription>
                  Graphics and designs for your campaign
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  {designAssets.map(asset => (
                    <div key={asset.id} className="border rounded-md p-2 relative group">
                      <img 
                        src={asset.url} 
                        alt="Campaign asset" 
                        className="w-full h-auto rounded-md" 
                      />
                      <div className="absolute inset-0 bg-black/60 rounded-md opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button variant="outline" size="sm" className="text-white border-white">
                          View
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          <Card>
            <CardHeader>
              <CardTitle>Campaign Strategy Tips</CardTitle>
              <CardDescription>Best practices for your campaign type</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <div className="space-y-4 pr-4">
                  <div>
                    <h3 className="text-sm font-medium flex items-center gap-2">
                      <Target className="h-4 w-4 text-purple-500" />
                      Pre-Launch Strategy
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Build anticipation 2-4 weeks before release with teaser content, 
                      behind-the-scenes material, and countdowns to maximize day-one impact.
                    </p>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h3 className="text-sm font-medium flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-purple-500" />
                      Content Recommendations
                    </h3>
                    <ul className="text-sm text-muted-foreground mt-1 list-disc pl-5 space-y-1">
                      <li>Share short snippets/teasers of your music</li>
                      <li>Post behind-the-scenes content from recording</li>
                      <li>Create artwork/visual reveal videos</li>
                      <li>Film a simple "making of" documentary</li>
                      <li>Engage fans with Q&A sessions about the release</li>
                    </ul>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h3 className="text-sm font-medium flex items-center gap-2">
                      <Users className="h-4 w-4 text-purple-500" />
                      Audience Engagement
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Boost engagement by creating interactive elements like polls, 
                      exclusive previews for superfans, and shareable content that 
                      encourages your audience to spread the word.
                    </p>
                  </div>
                  
                  <Separator />
                  
                  <div>
                    <h3 className="text-sm font-medium flex items-center gap-2">
                      <ImageIcon className="h-4 w-4 text-purple-500" />
                      Visuals & Aesthetics
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Maintain consistent visual branding across all platforms. 
                      Create templates for Instagram stories, post graphics, and 
                      video thumbnails that align with your release theme.
                    </p>
                  </div>
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Content Calendar</CardTitle>
              <CardDescription>Plan your content schedule</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-10 space-y-4">
                <Calendar
                  mode="single"
                  selected={releaseDate}
                  onSelect={setReleaseDate}
                  className="rounded-md border mx-auto"
                />
                <Button variant="outline" className="w-full gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  View Full Calendar
                </Button>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle>Resource Hub</CardTitle>
              <CardDescription>Tools & templates for your campaign</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <Button variant="outline" className="w-full justify-start gap-2 text-left">
                  <FileSpreadsheet className="h-4 w-4" />
                  <div className="flex flex-col items-start">
                    <span>Content Calendar Template</span>
                    <span className="text-xs text-muted-foreground">Excel/Google Sheets</span>
                  </div>
                </Button>
                
                <Button variant="outline" className="w-full justify-start gap-2 text-left">
                  <CheckSquare className="h-4 w-4" />
                  <div className="flex flex-col items-start">
                    <span>Release Checklist</span>
                    <span className="text-xs text-muted-foreground">PDF Guide</span>
                  </div>
                </Button>
                
                <Button variant="outline" className="w-full justify-start gap-2 text-left">
                  <ImageIcon className="h-4 w-4" />
                  <div className="flex flex-col items-start">
                    <span>Social Media Templates</span>
                    <span className="text-xs text-muted-foreground">Canva/Photoshop</span>
                  </div>
                </Button>
              </div>
            </CardContent>
            <CardFooter className="flex justify-center">
              <Button variant="link" className="text-sm">View all resources</Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CampaignPlanner;
